import React from "react";
import {
    MapContainer,
    TileLayer,
    GeoJSON,
    Tooltip,
    CircleMarker
} from "react-leaflet";
import "leaflet/dist/leaflet.css";

import mexicoStates from "../data/mexico-states.json";

const activeStates = {
    Veracruz: {
        color: "#e11d48",
        label: "Sede Central"
    },
    Oaxaca: {
        color: "#f97316",
        label: "Zona Atendidas"
    },
    Puebla: {
        color: "#f97316",
        label: "Zona Atendidas"
    },
    Tabasco: {
        color: "#fb923c",
        label: "Zona Atendidas"
    }
};

const normalizeStateName = (name) => {
    if (name && name.includes("Veracruz")) return "Veracruz";
    return name;
};

const stateStyle = (feature) => {
    const stateName = normalizeStateName(feature.properties.name);

    if (activeStates[stateName]) {
        return {
            fillColor: activeStates[stateName].color,
            fillOpacity: 0.7,
            color: "#ffffff",
            weight: 1.5,
            opacity: 1,
            dashArray: ""
        };
    }

    return {
        fillColor: "#e5e7eb",
        fillOpacity: 0.2,
        color: "#cbd5e1",
        weight: 0.5,
        opacity: 0.5
    };
};

const stateCoverage = {
    Veracruz: {
        municipalities: [
            "Xalapa (Sede Central)",
            "Puerto de Veracruz",
            "Boca del Río",
            "Martínez de la Torre",
            "Coatzacoalcos",
            "Poza Rica",
            "Córdoba",
            "Orizaba",
        ],
        highlights: [
            { name: "Xalapa", lat: 19.5438, lng: -96.9102, main: true },
            { name: "Puerto de Veracruz", lat: 19.1738, lng: -96.1342 },
            { name: "Boca del Río", lat: 19.1048, lng: -96.1077 },
            { name: "Martínez de la Torre", lat: 20.0676, lng: -97.0608 },
            { name: "Poza Rica", lat: 20.5331, lng: -97.4595 },
            { name: "Córdoba", lat: 18.8947, lng: -96.9330 },
            { name: "Orizaba", lat: 18.8523, lng: -97.0996 },
            { name: "Coatzacoalcos", lat: 18.1345, lng: -94.4589 },
        ]
    },
    Oaxaca: {
        municipalities: ["Puerto Escondido"],
        highlights: [{ name: "Puerto Escondido", lat: 15.8596, lng: -97.0720 }]
    },
    Puebla: {
        municipalities: ["Puebla"],
        highlights: [{ name: "Puebla", lat: 19.0414, lng: -98.2063 }]
    },
    Tabasco: {
        municipalities: ["Villahermosa"],
        highlights: [{ name: "Villahermosa", lat: 17.9892, lng: -92.9475 }]
    }
};

const onEachState = (feature, layer) => {
    const stateName = normalizeStateName(feature.properties.name);
    const coverage = stateCoverage[stateName];

    if (!activeStates[stateName]) {
        layer.options.interactive = false;
        return;
    }

    const municipalitiesHTML = coverage
        ? coverage.municipalities.map(m => `• ${m}`).join("<br/>")
        : "";

    layer.bindTooltip(
        `
        <div style="font-size:12px">
          <strong>${stateName}</strong><br/>
          <span style="opacity:.8">${activeStates[stateName].label}</span>
          <hr style="margin:4px 0"/>
          <strong>Municipios atendidos:</strong><br/>
          ${municipalitiesHTML}
        </div>
        `,
        { sticky: true, direction: "top", opacity: 1 }
    );

    layer.on({
        mousedown: (e) => {
            if (e.originalEvent) e.originalEvent.preventDefault();
        },
        click: (e) => {
            layer.openTooltip();
            if (e.target._path) e.target._path.blur();
        }
    });
};

const LeafletCoverageMap = () => {
    return (
        <section className="w-full bg-gray-50 py-20">
            <div className="max-w-7xl mx-auto px-4">
                <div className="text-center mb-10">
                    <h2 className="text-4xl font-extrabold uppercase tracking-tight italic text-gray-900">
                        Cobertura Técnica Nacional 🇲🇽
                    </h2>
                    <p className="mt-4 text-gray-600 italic max-w-3xl mx-auto">
                        Infraestructura logística optimizada para atención inmediata en el
                        corredor industrial del sureste mexicano.
                    </p>
                </div>

                <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl border border-gray-200">
                    <MapContainer
                        center={[19.5, -96.5]} 
                        zoom={6}
                        minZoom={5}
                        scrollWheelZoom={false}
                        className="w-full h-[600px] z-10"
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />

                        <GeoJSON
                            data={mexicoStates}
                            style={stateStyle}
                            onEachFeature={onEachState}
                        />

                        {Object.entries(stateCoverage).map(([state, data]) =>
                            data.highlights.map((city, idx) => (
                                <CircleMarker
                                    key={`${state}-${idx}`}
                                    center={[city.lat, city.lng]}
                                    radius={city.main ? 10 : 6}
                                    pathOptions={{
                                        color: city.main ? "#9f1239" : "#fb923c",
                                        fillColor: city.main ? "#e11d48" : "#fb923c",
                                        fillOpacity: city.main ? 0.9 : 0.7,
                                        weight: 2
                                    }}
                                >
                                    <Tooltip direction="top" opacity={1}>
                                        <strong>{city.name}</strong>
                                        {city.main && <div>Sede principal</div>}
                                    </Tooltip>
                                </CircleMarker>
                            ))
                        )}
                    </MapContainer>

                    <div className="absolute bottom-6 right-6 bg-white/90 backdrop-blur-md p-4 rounded-2xl shadow-lg text-xs space-y-2 z-[20]">
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-red-600"></span>
                            <span className="font-bold uppercase">Sede Central</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                            <span className="font-bold uppercase">Zonas de Operación</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default LeafletCoverageMap;
